import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import StockReturnSideMsg from '#/modules/imsMgmt/receipts/stockReturn/components/StockReturnSideMsg';
import { buildStockReturnColumns } from '#/modules/imsMgmt/receipts/stockReturn/data/stockReturnColumns';
import { getStockReturnApiErrorMessage } from '#/modules/imsMgmt/receipts/stockReturn/helper/apiError';
import { applyReturnDateRangeToFilters } from '#/modules/imsMgmt/receipts/stockReturn/helper/returnDateListFilter';
import useStockReturnList from '#/modules/imsMgmt/receipts/stockReturn/hooks/useStockReturnList';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import WorkOrderStockOpsPageHeader from '#/modules/momMgmt/workOrder/components/WorkOrderStockOpsPageHeader';

const WORK_ORDER_STOCK_RETURN_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  returnNo: '退料單號',
  returnDate: '退料日',
  status: '狀態',
};

const WorkOrderStockReturnListPage = () => {
  const listRef = useRef(null);
  const [sourceWarehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [targetWarehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.STOCK_RETURN_CREATE);
  const canUpdate = useCan(PERM.STOCK_RETURN_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useStockReturnList();

  const setReturnDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyReturnDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'returnDateFrom' && value === undefined) {
      setReturnDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setReturnDateRangeFilter]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      hydrateWarehouseFilterLabels(
        listQuery.filters?.sourceWarehouseId,
        sourceWarehouseLabelCache,
      ),
      hydrateWarehouseFilterLabels(
        listQuery.filters?.targetWarehouseId,
        targetWarehouseLabelCache,
      ),
    ]).then((results) => {
      if (!cancelled && results.some(Boolean)) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.sourceWarehouseId,
    listQuery.filters?.targetWarehouseId,
    sourceWarehouseLabelCache,
    targetWarehouseLabelCache,
  ]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildStockReturnColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onReturnDateRangeChange: setReturnDateRangeFilter,
        includeNavLinks: true,
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
        sourceWarehouseLabelCache,
        targetWarehouseLabelCache,
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setReturnDateRangeFilter,
      sourceWarehouseLabelCache,
      targetWarehouseLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.workOrderReturnCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <WorkOrderStockOpsPageHeader page="return" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getStockReturnApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋單號、備註、庫存編號或物料"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={WORK_ORDER_STOCK_RETURN_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>

          <StockReturnSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default WorkOrderStockReturnListPage;
